import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Approval } from '../types';
import { generatePDFFromElement } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { API_BASE_URL, API_ENDPOINTS, getFileURL } from '../config/apiClient';

const ApprovalDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token, user, handleUnauthorized } = useAuth();
  const [approval, setApproval] = useState<Approval | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openApproveDialog, setOpenApproveDialog] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [openReturnDialog, setOpenReturnDialog] = useState(false);
  const [comments, setComments] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState(false);

  // Check if current user is an approver
  const isApprover = user?.role?.name === 'Approvers';

  // Fetch approval details from Strapi
  useEffect(() => {
    const fetchApprovalDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch approval details
        const response = await fetch(
          `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.GET(id!)}?populate[itemrequests][populate]=*&populate[users_permissions_user][populate]=*`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            handleUnauthorized();
            return;
          }
          throw new Error(`Failed to fetch approval details: ${response.statusText}`);
        }

        const data = (await response.json())?.data;
        
        const itemrequests = data.itemrequests || [];
        const createdByUser = data.users_permissions_user || {};

        // Fetch all approvers with Approvers role
        let allApprovers: any[] = [];
        try {
          const approversResponse = await fetch(
            `${API_BASE_URL}${API_ENDPOINTS.USERS.GET_APPROVERS}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { Authorization: `Bearer ${token}` }),
              },
            }
          );

          if (approversResponse.ok) {
            const approversData = await approversResponse.json();
            allApprovers = (approversData.data || []).map((approver: any) => ({
              email: approver.email,
              username: approver.username,
            }));
          }
        } catch (err) {
          console.error('Error fetching approvers list:', err);
        }

        // Get current approvals from the approval request
        const rawApprovals = data.approvals || [];
        
        // Deduplicate approvals by email (keeping latest)
        const approvalsMapByEmail = new Map<string, any>();
        rawApprovals.forEach((approval: any) => {
          const emailKey = (approval.email || '').toLowerCase();
          // Always overwrite with the latest entry (Map naturally keeps last insert)
          approvalsMapByEmail.set(emailKey, approval);
        });

        // Build complete approver list
        // Track which emails have approvals to avoid showing them twice (approved + pending)
        const approvedEmails = new Set<string>();
        approvalsMapByEmail.forEach((approval: any) => {
          approvedEmails.add((approval.email || '').toLowerCase());
        });

        let completeApprovers: any[] = [];
        
        if (allApprovers.length > 0) {
          // If we fetched the approvers list, use that as the base
          completeApprovers = allApprovers
            .filter((approver: any) => {
              // Only include approvers who haven't already acted (not in approvedEmails)
              const emailKey = (approver.email || '').toLowerCase();
              return !approvedEmails.has(emailKey);
            })
            .map((approver: any) => {
              return {
                id: approver.email || 'unknown',
                name: `${approver.username}(${approver.email})` || 'Unknown',
                role: 'Approver',
                status: 'pending' as const,
                approvalDate: undefined,
                comments: undefined,
              };
            });

          // Prepend approvers who have already taken action
          const approvedApprovers = Array.from(approvalsMapByEmail.values()).map((approval: any) => {
            const approver = allApprovers.find((a: any) => (a.email || '').toLowerCase() === (approval.email || '').toLowerCase());
            const username = approval.username || approver?.username || 'Unknown';
            
            return {
              id: approval.email || 'unknown',
              name: `${username}(${approval.email})` || 'Unknown',
              role: 'Approver',
              status: (approval.status || 'Pending').toLowerCase() as
                | 'approved'
                | 'rejected'
                | 'pending'
                | 'returned',
              approvalDate: approval.approvalDate || undefined,
              comments: approval.comments || undefined,
            };
          });

          completeApprovers = [...approvedApprovers, ...completeApprovers];
        } else if (rawApprovals.length > 0) {
          // Fallback: if we couldn't fetch the users list, just use current approvals
          completeApprovers = Array.from(approvalsMapByEmail.values()).map((approval: any) => {
            const username = approval.username || 'Unknown';
            return {
              id: approval.email || 'unknown',
              name: `${username}(${approval.email})` || 'Unknown',
              role: 'Approver',
              status: (approval.status || 'Pending').toLowerCase() as
                | 'approved'
                | 'rejected'
                | 'pending'
                | 'returned',
              approvalDate: approval.approvalDate || undefined,
              comments: approval.comments || undefined,
            };
          });
        }

        const transformedApproval: Approval = {
          id: data.documentId,
          title: data.title || `Approval Request #${data.documentId}`,
          items: itemrequests.map((ir: any) => {
            const costCategoryId = ir?.cost_category?.documentId;
            
            return {
              itemName: ir?.item_estimate?.item || 'Unknown Item',
              quantity: ir.quantity || 0,
              rate: ir.unitPrice || 0,
              totalAmount: ir.totalPrice || (ir.quantity || 0) * (ir.unitPrice || 0),
              costCategoryId: costCategoryId,
              comment: ir.comment || undefined,
              files: (ir?.attachment || []).map((file: any) => ({
                name: file.name || 'Unknown',
                size: file.size || 0,
                type: file.mime || '',
                uploadedAt: file.createdAt || new Date().toISOString(),
                url: file.url || '',
              })),
              previousApprovals: 0, // Will be set after fetching previous approvals
              currentEstimationAllocation: ir?.cost_category?.estimatedCost || 0,
            };
          }),
          totalAmount:
            itemrequests.reduce(
              (sum: number, ir: any) =>
                sum + (ir.totalPrice || 0),
              0
            ) || 0,
          status: (
            data.approvalStatus || 'Pending'
          ).toLowerCase() as 'pending' | 'approved' | 'rejected' | 'returned',
          requestedBy: `${createdByUser?.username}(${createdByUser?.email})` || 'Unknown User',
          createdAt: data.createdAt
            ? new Date(data.createdAt).toLocaleDateString()
            : 'N/A',
          approvers: completeApprovers,
        };

        console.log('Approval Details:', transformedApproval);
        console.log('Complete Approvers:', completeApprovers);

        // Fetch previous approvals for each cost category with breakdown
        const approvalsMap: Record<string, Array<{ amount: number; approvalId: string }>> = {};
        try {
          // Get unique cost category IDs from current items
          const costCategoryIds = [...new Set(
            transformedApproval.items
              .filter(item => item.costCategoryId)
              .map(item => item.costCategoryId)
          )];

          // Fetch all approved approval requests to calculate previous approvals
          if (costCategoryIds.length > 0) {
            const approvedApprovalsResponse = await fetch(
              `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.LIST}?filters[approvalStatus][$eq]=Approved&pagination[limit]=1000&populate[itemrequests][populate]=cost_category`,
              {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  ...(token && { Authorization: `Bearer ${token}` }),
                },
              }
            );

            if (approvedApprovalsResponse.ok) {
              const approvedApprovalsData = await approvedApprovalsResponse.json();
              const approvedApprovals = approvedApprovalsData.data || [];

              // Calculate approved amounts per cost category with breakdown (excluding current approval)
              approvedApprovals.forEach((approval: any) => {
                if (approval.documentId !== id) {
                  const itemrequests = approval.itemrequests || [];
                  itemrequests.forEach((ir: any) => {
                    const costCatId = ir?.cost_category?.documentId;
                    if (costCatId && costCategoryIds.includes(costCatId)) {
                      if (!approvalsMap[costCatId]) {
                        approvalsMap[costCatId] = [];
                      }
                      approvalsMap[costCatId].push({
                        amount: ir.totalPrice || 0,
                        approvalId: approval.documentId,
                      });
                    }
                  });
                }
              });
            }
          }
        } catch (err) {
          console.error('Error fetching previous approvals:', err);
        }

        // Update items with previous approvals
        const finalApproval = {
          ...transformedApproval,
          items: transformedApproval.items.map(item => ({
            ...item,
            previousApprovals: item.costCategoryId ? approvalsMap[item.costCategoryId] || [] : [],
          })),
        };

        setApproval(finalApproval);
      } catch (err) {
        console.error('Error fetching approval details:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to fetch approval details'
        );
      } finally {
        setLoading(false);
      }
    };

    if (id && token) {
      fetchApprovalDetails();
    }
  }, [id, token]);

  const handleDownloadPDF = async () => {
    try {
      setDownloadingPDF(true);
      const timestamp = new Date().toLocaleString('en-IN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).replace(/[/:]/g, '-');
      
      const filename = `Approval_Report_${approval?.title?.replace(/\s+/g, '_') || 'Request'}_${timestamp}.pdf`;
      
      // Only collect document file attachments (PDFs, Excel, etc.), not images
      // Images are already embedded in the PDF content at reduced quality
      const allAttachments: any[] = [];
      if (approval?.items) {
        approval.items.forEach(item => {
          if (item.files && item.files.length > 0) {
            item.files.forEach(file => {
              // Only include non-image files in separate attachments
              if (!file.type.startsWith('image/')) {
                allAttachments.push({
                  name: file.name,
                  url: file.url,
                  type: file.type,
                  size: file.size,
                });
              }
            });
          }
        });
      }
      
      await generatePDFFromElement('approval-pdf-content', filename, {
        orientation: 'portrait',
        format: 'a4',
        margin: 10,
      }, allAttachments);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/budget-approval')}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  if (!approval) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5" color="error">
          Approval not found
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/budget-approval')}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const determineApprovalStatus = (currentApprovals: any[], newApprovalEntry: any) => {
    // Combine current approvals with the new one
    const allApprovals = [...currentApprovals, newApprovalEntry];

    // Count approvals by status
    const approvedCount = allApprovals.filter((a) => a.status === 'Approved').length;
    const rejectedCount = allApprovals.filter((a) => a.status === 'Rejected').length;
    const returnedCount = allApprovals.filter((a) => a.status === 'Returned').length;

    // Determine new status based on count of approvals
    if (approvedCount >= 3) {
      return 'Approved';
    } else if (rejectedCount >= 3) {
      return 'Rejected';
    } else if (returnedCount >= 3) {
      return 'Returned';
    } else {
      return 'Pending';
    }
  };

  const handleApprove = async () => {
    if (!approval || !user) return;

    try {
      const newApprovalEntry = {
        email: user.email,
        username: user.username,
        status: 'Approved',
        approvalDate: new Date().toLocaleString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        }),
        comments: comments || '',
      };

      // Keep existing approvals and update/add the current user's approval
      const existingApprovals = approval.approvers
        .filter((a) => {
          // Extract email from name format "username(email)" and compare
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          return extractedEmail !== user?.email;
        })
        .map((a) => {
          // Extract email from name format "username(email)"
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          const usernameMatch = a.name.match(/^([^(]+)/);
          const extractedUsername = usernameMatch ? usernameMatch[1].trim() : 'Unknown';
          
          return {
            email: extractedEmail,
            username: extractedUsername,
            status: a.status.charAt(0).toUpperCase() + a.status.slice(1),
            approvalDate: a.approvalDate,
            comments: a.comments,
          };
        });
      const updatedApprovals = [...existingApprovals, newApprovalEntry];
      const newApprovalStatus = determineApprovalStatus(updatedApprovals, {});

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.UPDATE(id!)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            data: {
              approvals: updatedApprovals,
              approvalStatus: newApprovalStatus,
            },
          }),
        }
      );

      if (response.ok) {
        setOpenApproveDialog(false);
        setComments('');
        // Refetch to get updated data
        window.location.reload();
      }
    } catch (err) {
      console.error('Error approving request:', err);
    }
  };

  const handleReject = async () => {
    if (!approval || !user) return;

    try {
      const newApprovalEntry = {
        email: user.email,
        username: user.username,
        status: 'Rejected',
        approvalDate: new Date().toLocaleString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        }),
        comments: comments || '',
      };

      // Keep existing approvals and update/add the current user's approval
      const existingApprovals = approval.approvers
        .filter((a) => {
          // Extract email from name format "username(email)" and compare
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          return extractedEmail !== user?.email;
        })
        .map((a) => {
          // Extract email from name format "username(email)"
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          const usernameMatch = a.name.match(/^([^(]+)/);
          const extractedUsername = usernameMatch ? usernameMatch[1].trim() : 'Unknown';
          
          return {
            email: extractedEmail,
            username: extractedUsername,
            status: a.status.charAt(0).toUpperCase() + a.status.slice(1),
            approvalDate: a.approvalDate,
            comments: a.comments,
          };
        });
      const updatedApprovals = [...existingApprovals, newApprovalEntry];
      const newApprovalStatus = determineApprovalStatus(updatedApprovals, {});

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.UPDATE(id!)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            data: {
              approvals: updatedApprovals,
              approvalStatus: newApprovalStatus,
            },
          }),
        }
      );

      if (response.ok) {
        setOpenRejectDialog(false);
        setComments('');
        // Refetch to get updated data
        window.location.reload();
      }
    } catch (err) {
      console.error('Error rejecting request:', err);
    }
  };

  const handleReturn = async () => {
    if (!approval || !user) return;

    try {
      const newApprovalEntry = {
        email: user.email,
        username: user.username,
        status: 'Returned',
        approvalDate: new Date().toLocaleString('en-IN', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        }),
        comments: comments || '',
      };

      // Keep existing approvals and update/add the current user's approval
      const existingApprovals = approval.approvers
        .filter((a) => {
          // Extract email from name format "username(email)" and compare
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          return extractedEmail !== user?.email;
        })
        .map((a) => {
          // Extract email from name format "username(email)"
          const emailMatch = a.name.match(/\(([^)]+)\)/);
          const extractedEmail = emailMatch ? emailMatch[1] : a.name;
          const usernameMatch = a.name.match(/^([^(]+)/);
          const extractedUsername = usernameMatch ? usernameMatch[1].trim() : 'Unknown';
          
          return {
            email: extractedEmail,
            username: extractedUsername,
            status: a.status.charAt(0).toUpperCase() + a.status.slice(1),
            approvalDate: a.approvalDate,
            comments: a.comments,
          };
        });
      const updatedApprovals = [...existingApprovals, newApprovalEntry];
      const newApprovalStatus = determineApprovalStatus(updatedApprovals, {});

      const response = await fetch(
        `${API_BASE_URL}${API_ENDPOINTS.APPROVAL_REQUESTS.UPDATE(id!)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({
            data: {
              approvals: updatedApprovals,
              approvalStatus: newApprovalStatus,
            },
          }),
        }
      );

      if (response.ok) {
        setOpenReturnDialog(false);
        setComments('');
        // Refetch to get updated data
        window.location.reload();
      }
    } catch (err) {
      console.error('Error returning request:', err);
    }
  };

  const isImageFile = (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
  };

  const isPending = approval.approvers.some((a) => a.status === 'pending');

  // Check if current user has already submitted their approval
  const hasUserAlreadyApproved = approval.approvers.some((a) => {
    // Extract email from name format "username(email)"
    const emailMatch = a.name.match(/\(([^)]+)\)/);
    const extractedEmail = emailMatch ? emailMatch[1] : a.name;
    return extractedEmail === user?.email && a.status !== 'pending';
  });

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/budget-approval')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadPDF}
            disabled={downloadingPDF || !approval}
          >
            {downloadingPDF ? 'Generating PDF...' : 'Download Report'}
          </Button>
        </Box>

      <div id="approval-pdf-content">
        <Paper sx={{ p: 4, mb: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {approval.title || 'Approval Details'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              ID: {approval.id}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Request ID
              </Typography>
              <Typography variant="h6">#{approval.id}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Status
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  mt: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 1,
                  backgroundColor:
                    approval.status === 'approved'
                      ? '#e8f5e9'
                      : approval.status === 'rejected'
                      ? '#ffebee'
                      : approval.status === 'returned'
                      ? '#e1f5fe'
                      : '#fffde7',
                  color:
                    approval.status === 'approved'
                      ? '#2e7d32'
                      : approval.status === 'rejected'
                      ? '#c62828'
                      : approval.status === 'returned'
                      ? '#01579b'
                      : '#f57f17',
                  border: '1.5px solid',
                  borderColor:
                    approval.status === 'approved'
                      ? '#2e7d32'
                      : approval.status === 'rejected'
                      ? '#c62828'
                      : approval.status === 'returned'
                      ? '#01579b'
                      : '#f57f17',
                  fontWeight: 500,
                }}
              >
                {approval.status === 'approved' ? (
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                ) : approval.status === 'rejected' ? (
                  <CancelIcon sx={{ fontSize: 20 }} />
                ) : approval.status === 'returned' ? (
                  <HelpOutlineIcon sx={{ fontSize: 20 }} />
                ) : approval.status === 'pending' ? (
                  <AccessTimeIcon sx={{ fontSize: 20 }} />
                ) : null}
                {approval.status.charAt(0).toUpperCase() + approval.status.slice(1)}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Total Amount
              </Typography>
              <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                ₹{approval.totalAmount.toFixed(2)}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Requested By
              </Typography>
              <Typography variant="body1">{approval.requestedBy}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold' }}>
                Requested Date
              </Typography>
              <Typography variant="body1">{approval.createdAt}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 'bold', mb: 2 }}>
                Items ({approval.items.length})
              </Typography>
              <TableContainer sx={{ border: '1px solid #ddd', borderRadius: 1 }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 'bold' }}>Item Name</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Quantity
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Rate
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Previous Approvals
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Current Estimation
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {approval.items.map((item, idx) => (
                      <React.Fragment key={idx}>
                        <TableRow>
                          <TableCell>{item.itemName}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">₹{item.rate.toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            ₹{item.totalAmount.toFixed(2)}
                          </TableCell>
                          <TableCell align="right">
                            {item.previousApprovals && item.previousApprovals.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {item.previousApprovals.map((breakdown, idx) => (
                                  <Box key={idx}>
                                    <a
                                      href={`/approval/${breakdown.approvalId}`}
                                      style={{
                                        color: '#1976d2',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontSize: 'inherit',
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#1565c0';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#1976d2';
                                      }}
                                    >
                                      ₹{breakdown.amount.toFixed(2)}
                                    </a>
                                    {idx < (item.previousApprovals?.length ?? 0) - 1 && ' + '}
                                  </Box>
                                ))}
                                {(item.previousApprovals?.length ?? 0) > 0 && (
                                  <Box sx={{ borderTop: '1px solid #ddd', pt: 0.5, fontWeight: 'bold' }}>
                                    = ₹{(item.previousApprovals ?? []).reduce((sum, b) => sum + b.amount, 0).toFixed(2)}
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No previous approvals
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            ₹{(item.currentEstimationAllocation || 0).toFixed(2)}
                          </TableCell>
                        </TableRow>
                        {(item.files && item.files.length > 0) || item.comment ? (
                          <TableRow>
                            <TableCell colSpan={6}>
                              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', py: 1, alignItems: 'flex-start' }}>
                                {item.files && item.files.length > 0 && (
                                  <Box sx={{ flex: '1 1 auto', minWidth: '200px' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
                                      Attachments:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                      {item.files.map((file, fileIdx) => (
                                        <Box
                                          key={fileIdx}
                                          sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            gap: 1,
                                          }}
                                        >
                                          {isImageFile(file.type) ? (
                                            <>
                                              {file.url && (
                                                <Box
                                                  component="img"
                                                  src={getFileURL(file.url)}
                                                  alt={file.name}
                                                  sx={{
                                                    maxWidth: '120px',
                                                    maxHeight: '120px',
                                                    borderRadius: 1,
                                                    border: '1px solid #ddd',
                                                    cursor: 'pointer',
                                                    objectFit: 'cover',
                                                  }}
                                                />
                                              )}
                                              {file.url && (
                                                <Button
                                                  size="small"
                                                  variant="text"
                                                  href={getFileURL(file.url)}
                                                  download={file.name}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  sx={{
                                                    textTransform: 'none',
                                                    maxWidth: '120px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                  }}
                                                >
                                                  {file.name}
                                                </Button>
                                              )}
                                            </>
                                          ) : (
                                            <>
                                              {file.url && (
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  href={getFileURL(file.url)}
                                                  download={file.name}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  sx={{ textTransform: 'none' }}
                                                >
                                                  📎 {file.name}
                                                </Button>
                                              )}
                                            </>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                                {item.comment && (
                                  <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 0.5 }}>
                                      💬 Comment:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#333', fontStyle: 'italic' }}>
                                      {item.comment}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : null}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 4, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Approval Chain
        </Typography>

        {approval?.approvers && approval.approvers.length > 0 ? (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Approver Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '15%' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>Decided on</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {approval.approvers.map((approver) => (
                  <TableRow key={approver.id}>
                    <TableCell sx={{ width: '25%', wordBreak: 'break-word' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {approver.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '12%' }}>{approver.role}</TableCell>
                    <TableCell sx={{ width: '15%' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 0.75,
                          backgroundColor:
                            approver.status === 'approved'
                              ? '#e8f5e9'
                              : approver.status === 'rejected'
                              ? '#ffebee'
                              : approver.status === 'returned'
                              ? '#e1f5fe'
                              : '#fffde7',
                          color:
                            approver.status === 'approved'
                              ? '#2e7d32'
                              : approver.status === 'rejected'
                              ? '#c62828'
                              : approver.status === 'returned'
                              ? '#01579b'
                              : '#f57f17',
                          border: '1.5px solid',
                          borderColor:
                            approver.status === 'approved'
                              ? '#2e7d32'
                              : approver.status === 'rejected'
                              ? '#c62828'
                              : approver.status === 'returned'
                              ? '#01579b'
                              : '#f57f17',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        {approver.status === 'approved' ? (
                          <CheckCircleIcon sx={{ fontSize: 18 }} />
                        ) : approver.status === 'rejected' ? (
                          <CancelIcon sx={{ fontSize: 18 }} />
                        ) : approver.status === 'returned' ? (
                          <HelpOutlineIcon sx={{ fontSize: 18 }} />
                        ) : approver.status === 'pending' ? (
                          <AccessTimeIcon sx={{ fontSize: 18 }} />
                        ) : null}
                        {approver.status.charAt(0).toUpperCase() + approver.status.slice(1)}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: '18%' }}>{approver.approvalDate || '-'}</TableCell>
                    <TableCell sx={{ width: '30%', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                        {approver.comments || '-'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No approval entries yet.
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 4, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
          Attachments
        </Typography>

        {approval?.items && approval.items.some(item => item.files && item.files.length > 0) ? (
          <Box>
            {approval.items.map((item, itemIndex) => {
              const hasAttachments = item.files && item.files.length > 0;
              if (!hasAttachments) return null;

              return (
                <Box key={itemIndex} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {item.itemName}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    {item.files!.map((file, fileIndex) => (
                      <Box key={fileIndex}>
                        {isImageFile(file.type) && file.url ? (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                              {file.name} ({(file.size / 1024).toFixed(2)} KB)
                            </Typography>
                            <Box
                              component="img"
                              src={getFileURL(file.url)}
                              alt={file.name}
                              sx={{
                                maxWidth: '300px',
                                maxHeight: '400px',
                                borderRadius: 1,
                                border: '1px solid #ddd',
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd', minWidth: '200px' }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              📄 {file.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
                              Size: {(file.size / 1024).toFixed(2)} KB | Type: {file.type}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            No attachments available.
          </Typography>
        )}
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to approve this request?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments (Optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments or notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to reject this request?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason (Required)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            error={!comments}
            helperText={!comments ? 'Reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!comments}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return for Clarification Dialog */}
      <Dialog open={openReturnDialog} onClose={() => setOpenReturnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return for Clarification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to return this request for clarification?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Clarification Required (Required)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Please provide specific clarifications or information needed..."
            error={!comments}
            helperText={!comments ? 'Please specify what clarifications are needed' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenReturnDialog(false);
            setComments('');
          }}>Cancel</Button>
          <Button
            onClick={handleReturn}
            variant="contained"
            color="info"
            disabled={!comments}
          >
            Return for Clarification
          </Button>
        </DialogActions>
      </Dialog>
      </div>

      {/* Action Buttons Section (Not included in PDF) */}
      <Paper sx={{ p: 4, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
          Actions
        </Typography>
        {isApprover && isPending && !hasUserAlreadyApproved ? (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip title="Approve this request if you agree with it">
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setOpenApproveDialog(true)}
                  disabled={!isApprover || hasUserAlreadyApproved}
                >
                  Approve
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Reject this request if you disagree with it">
              <span>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setOpenRejectDialog(true)}
                  disabled={!isApprover || hasUserAlreadyApproved}
                >
                  Reject
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Return this request for clarification">
              <span>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<HelpOutlineIcon />}
                  onClick={() => setOpenReturnDialog(true)}
                  disabled={!isApprover || hasUserAlreadyApproved}
                >
                  Return for Clarification
                </Button>
              </span>
            </Tooltip>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: '#666' }}>
            {!isApprover ? (
              'You do not have the required role to perform approval actions.'
            ) : hasUserAlreadyApproved ? (
              'You have already submitted your approval decision.'
            ) : (
              'No pending approvals.'
            )}
          </Typography>
        )}
        {!isApprover && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#fff3cd', borderRadius: 1, border: '1px solid #ffc107' }}>
            <Typography variant="body2" sx={{ color: '#856404' }}>
              <strong>Note:</strong> You don't have the Approvers role. Only users with the Approvers role can approve, reject, or request clarifications on this request.
            </Typography>
          </Box>
        )}
        {hasUserAlreadyApproved && isApprover && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#d4edda', borderRadius: 1, border: '1px solid #28a745' }}>
            <Typography variant="body2" sx={{ color: '#155724' }}>
              <strong>Note:</strong> You have already submitted your approval decision. Your action is final and cannot be changed.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={openApproveDialog} onClose={() => setOpenApproveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to approve this request?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Comments (Optional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add any comments or notes..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApproveDialog(false)}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openRejectDialog} onClose={() => setOpenRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to reject this request?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Reason (Required)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            error={!comments}
            helperText={!comments ? 'Reason is required' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRejectDialog(false)}>Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!comments}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Return for Clarification Dialog */}
      <Dialog open={openReturnDialog} onClose={() => setOpenReturnDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return for Clarification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Are you sure you want to return this request for clarification?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Clarification Required (Required)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Please provide specific clarifications or information needed..."
            error={!comments}
            helperText={!comments ? 'Please specify what clarifications are needed' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenReturnDialog(false);
            setComments('');
          }}>Cancel</Button>
          <Button
            onClick={handleReturn}
            variant="contained"
            color="info"
            disabled={!comments}
          >
            Return for Clarification
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </Box>
  );
};

export default ApprovalDetails;
